import type { ActionType, LifecycleState } from "@/core/tma";

export interface BulkActionLoad {
  id: string;
  reference: string;
  lifecycleState: LifecycleState;
  customerSensitive: boolean;
  exceptionCount?: number;
  hoursSinceLastUpdate?: number;
}

export interface BulkActionSuggestion {
  actionType: ActionType;
  title: string;
  reason: string;
  loadIds: string[];
  requiresApproval: boolean;
}

const approvalRequiredActions = new Set<ActionType>([
  "send_customer_update",
  "book_carrier",
  "tender_carrier",
  "generate_customer_invoice",
  "run_carrier_settlement_batch",
  "open_osd_claim",
  "open_cargo_claim",
  "bill_tonu",
]);

export function suggestBulkActions(loads: BulkActionLoad[]): BulkActionSuggestion[] {
  return [
    suggestion("request_eta", "Request ETA", "In-transit loads stale for more than six hours", staleInTransitLoads(loads)),
    suggestion("request_pod", "Request POD", "Delivered loads still waiting on paperwork", podCandidateLoads(loads)),
    suggestion(
      "request_pickup_confirmation",
      "Confirm pickup readiness",
      "Covered loads approaching pickup",
      loads.filter((load) => load.lifecycleState === "covered").map((load) => load.id),
    ),
    suggestion(
      "send_customer_update",
      "Draft customer exception update",
      "Customer-visible exception summary for review",
      loads.filter((load) => (load.exceptionCount ?? 0) > 0 || load.customerSensitive).map((load) => load.id),
    ),
  ].filter((item) => item.loadIds.length > 0);
}

export function summarizeBulkAction(suggestion: BulkActionSuggestion, loads: BulkActionLoad[]) {
  const references = new Set(loads.filter((load) => suggestion.loadIds.includes(load.id)).map((load) => load.reference));
  return `${suggestion.title}: ${references.size} loads (${[...references].slice(0, 3).join(", ")}${references.size > 3 ? ", ..." : ""})`;
}

function staleInTransitLoads(loads: BulkActionLoad[]) {
  return loads
    .filter((load) => load.lifecycleState === "in_transit" && (load.hoursSinceLastUpdate ?? 0) >= 6)
    .map((load) => load.id);
}

function podCandidateLoads(loads: BulkActionLoad[]) {
  return loads
    .filter((load) => load.lifecycleState === "delivered" || load.lifecycleState === "pod_collecting")
    .map((load) => load.id);
}

function suggestion(
  actionType: ActionType,
  title: string,
  reason: string,
  loadIds: string[],
): BulkActionSuggestion {
  return {
    actionType,
    title,
    reason,
    loadIds,
    requiresApproval: approvalRequiredActions.has(actionType),
  };
}
